import { is, List } from "immutable";
import _ from "lodash";
import * as moment from "moment";
import * as QueryString from "querystringify";
import * as React from "react";
import * as BS from "react-bootstrap";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import * as RS from "react-sortable-hoc";
import * as redux from "redux";
import { DayView, EventEditModal, Filters, FilterType } from "./components";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import { AbstractDetailsModal } from "../../../containers/adminAbstracts";
import * as M from "../../../models";
import { EventLevels } from "../../../models/event";
import { Api, ApiEndpoint } from "../../../services/Api";
import Settings from "../../../settings";
import { AppState } from "../../../store";
import { DocumentHelper, ReactHelper, Utility } from "../../../utils";
import { EventViewSwitcher } from "../components";

interface UrlQuery {
    abstract?: string;
    day?: string;
    edit?: string;
    q?: string;
}

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultStatePropsMap(state, ownProps),
    events: state.adminEvents.events,
    loadingEvent: state.adminEvents.loadingEvent,
    loadingSubEvents: state.adminEvents.loadingSubEvents,
    subEvents: state.adminEvents.subEvents,
    subscribedAbstracts: state.abstract.subscribedEventAbstracts,
    subscribedEvents: state.adminEvents.subscribedEvents,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
    loadEvent: (eventId: number) => dispatch(Actions.AdminEvent.loadEvent(eventId)),
    loadSubEvents: (eventId: number) => dispatch(Actions.AdminEvent.loadSubEvents(eventId)),
    removeEvent: (evt: M.Event.Event) => dispatch(Actions.AdminEvent.removeEvent(evt)),
    removeSubEvent: (evt: M.Event.SubEvent) => dispatch(Actions.AdminEvent.removeSubEvent(evt)),
    subscribeToAbstracts: (eventId: number) => dispatch(Actions.Abstract.subscribeToEventAbstracts(eventId)),
    subscribeToEvent: (eventId: number) => dispatch(Actions.AdminEvent.subscribeToEvent(eventId)),
    subscribeToRsvpUpdates: (eventId: number) => dispatch(Actions.Event.subscribeToRsvpUpdates(eventId)),
    toggleLoadingEvent: (eventId: number, loading: boolean) => dispatch(Actions.AdminEvent.toggleLoadingEvent(eventId, loading)),
    toggleLoadingSubEvents: (loading: boolean) => dispatch(Actions.AdminEvent.toggleLoadingSubEvents(loading)),
    updateSubEvent: (evt: M.Event.SubEvent) => dispatch(Actions.AdminEvent.updateSubEvent(evt)),
});

type OwnProps = RouteComponentProps<{
    id: string;
}> & {};

type ConnectedState = Comp.Base.DefaultConnectedState & {
    events: List<M.Event.Event>;
    loadingEvent: M.Generic.ItemLoadingState;
    loadingSubEvents: boolean;
    subEvents: List<M.Event.SubEvent>;
    subscribedAbstracts: M.Generic.ResourceUpdated;
    subscribedEvents: M.Generic.ResourceUpdated;
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
    loadEvent: (eventId: number) => void;
    loadSubEvents: (eventId: number) => void;
    removeEvent: (evt: M.Event.Event) => void;
    removeSubEvent: (evt: M.Event.SubEvent) => void;
    subscribeToAbstracts: (eventId: number) => void;
    subscribeToEvent: (eventId: number) => void;
    subscribeToRsvpUpdates: (eventId: number) => void;
    toggleLoadingEvent: (eventId: number, loading: boolean) => void;
    toggleLoadingSubEvents: (loading: boolean) => void;
    updateSubEvent: (evt: M.Event.SubEvent) => void;
};

interface OwnState {
    appliedFilters: M.Search.AppliedFilter[];
    calendar: List<M.Event.CalendarDay>;
    days: M.Search.SearchFilter[];
    doFilter: boolean;
    event?: M.Event.Event;
    filteredCalendar: List<M.Event.CalendarDay>;
    generatingCalExport: boolean;
    generatingRsvpExport: boolean;
    isSessionSearch: boolean;
    search: string;
    subEvents: List<M.Event.SubEvent>;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class EditCalendarComponent extends Comp.Base.BaseComponent<CombinedProps, OwnState> {

    private _abstractDetailsModal: AbstractDetailsModal | null = null;
    private _confirmDelete: Comp.Modal.ConfirmModal | null = null;
    private _eventEditModal: EventEditModal | null = null;
    private _searchTimeout: NodeJS.Timer;

    public constructor(props: CombinedProps) {
        super(props);
        const urlQuery = QueryString.parse(props.location.search) as UrlQuery;

        this.state = {
            appliedFilters: [],
            calendar: List(),
            days: [],
            doFilter: false,
            filteredCalendar: List(),
            generatingCalExport: false,
            generatingRsvpExport: false,
            isSessionSearch: false,
            search: urlQuery.q || "",
            subEvents: List(),
        };
    }

    public async componentDidMount() {
        this._isMounted = true;
        await this.initializeComponent();
        const eventId = this.getEventId();
        this.props.subscribeToRsvpUpdates(eventId);
        this.props.subscribeToEvent(eventId);
    }

    public componentDidUpdate(prevProps: Readonly<CombinedProps>) {
        const eventId = this.getEventId();

        if (!is(prevProps.events, this.props.events))
            this.loadEventFromState();

        if (!is(prevProps.subEvents, this.props.subEvents))
            this.loadSubEventsFromState();

        if (!is(prevProps.websocket, this.props.websocket)) {
            this.props.subscribeToAbstracts(eventId);
            this.props.subscribeToEvent(eventId);
        }

        if (!is(prevProps.subscribedEvents[eventId], this.props.subscribedEvents[eventId])) {
            this.props.loadEvent(eventId);
            this.props.loadSubEvents(eventId);
        }

        if (!is(prevProps.subscribedAbstracts[eventId], this.props.subscribedAbstracts[eventId]))
            this.props.loadSubEvents(eventId);
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public shouldComponentUpdate?(nextProps: Readonly<OwnProps>, nextState: Readonly<OwnState>, nextContext: any): boolean {
        if (nextState.doFilter) {
            this.filterData();
            return false;
        }

        if (nextProps.location.search !== this.props.location.search) {
            this.updateAllFilterChecks(nextProps.location.search);
            return false;
        }

        return true;
    }

    public render() {
        const evt = this.state.event;
        const eventName = evt ? evt.title : "Event";
        const eventId = this.getEventId();
        const loading = this.state.loading || !!this.props.loadingEvent[eventId] || this.props.loadingSubEvents;

        const actionsCommon = <EventViewSwitcher abstractsEnabled={!!(evt && evt.abstractSettings)} eventId={eventId} view="Calendar" />;
        const lastPathIsEventList = this.lastPathIsFrom(this.props.lastPath, Paths.AdminEvent.List);
        const eventListLink = lastPathIsEventList
            ? <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); window.history.back(); }}>Events</a>
            : <Link to={Paths.AdminEvent.List}>Events</Link>;

        const actionsPrimary = evt
            ? <BS.ButtonToolbar>
                <BS.ButtonGroup>
                    <BS.Button onClick={() => this.updateUrlEditParam(evt.id, 0)}>
                        Add event
                    </BS.Button>
                    <BS.Button disabled={this.state.generatingCalExport} onClick={() => this.generateCalendarExport()}>
                        {this.state.generatingCalExport ? <><Comp.Generic.LoadingIcon /> Generating export&hellip;</> : "Export"}
                    </BS.Button>
                </BS.ButtonGroup>
            </BS.ButtonToolbar>
            : null;

        const actionsSecondary = <div className="paging-tools">
            <div id="pagination">
                <div className="input-group results-search">
                    <input
                        className="form-control"
                        type="text"
                        placeholder="Search schedule..."
                        onChange={(e) => this.handleSearch(e.target.value)}
                        value={this.state.search || ""}
                    />
                    <div className="input-group-btn">
                        <BS.Button disabled={!this.state.search} onClick={() => this.handleSearch("")} >
                            <i className="fa fa-times"></i>
                        </BS.Button>
                    </div>
                </div>
            </div>
        </div>;

        const pageTitle = <h1>
            {eventListLink} &raquo;{" "}
            <Link to={Paths.AdminEventEdit(eventId)}>{eventName}</Link> &raquo;{" "}
            Calendar
        </h1>;

        const days = evt
            ? this.state.filteredCalendar.map((day: M.Event.CalendarDay, i) => (
                <DayView
                    key={i}
                    day={day}
                    isSearch={this.state.isSessionSearch}
                    onEventDeleteClick={(subEvt) => this.toggleDeleteConfirmation(subEvt)}
                    onEventEditClick={(dayEvt) => this.updateUrlEditParam(evt.id, dayEvt ? dayEvt.id : 0)}
                    onRsvpExportClick={(evtId) => this.generateRsvpExport(evtId)}
                    onSessionEditClick={(parentEvt, session) => this.updateUrlEditParam(parentEvt.id, session ? session.id : 0)}
                    onSessionAbstractClick={(abstractId) => this.updateUrlAbstractParam(abstractId)}
                    onSessionsSorted={(parentId, sortEnd) => this.onSessionsSorted(parentId, sortEnd)}
                />
            ))
            : null;

        return <Comp.Page.AdminPage
            actionsCommon={actionsCommon}
            actionsPrimary={actionsPrimary}
            actionsSecondary={actionsSecondary}
            mainClass="results"
            preContent={pageTitle}
            title={"Events"}
        >
            <AbstractDetailsModal
                onClose={() => this.updateUrlAbstractParam()}
                ref={(ref) => this._abstractDetailsModal = ref}
            />

            <Comp.Modal.ConfirmModal
                headerClassName="warning"
                onProceed={() => this.handleDelete()}
                title="Delete event"
                renderBody={(data: M.Event.SubEvent) => {
                    const evtLevel = data && EventLevels.isSession(data) ? "session" : "event";
                    return <p>Are you sure you want to delete this {evtLevel}?</p>;
                }}
                ref={(ref) => this._confirmDelete = ref}
            />

            {this.getErrorAlert()}

            <Comp.Generic.LoadingSpinner show={loading} />

            <EventEditModal
                api={this.props.api}
                eventId={eventId}
                onClosed={() => this.updateUrlEditParam()}
                onProcessing={() => {}}
                onUpdated={(subEvt) => this.onSubEventUpdated(subEvt)}
                ref={(ref) => this._eventEditModal = ref}
            />

            <Filters
                days={this.state.days}
                loadingEvent={loading}
                onFilterSelect={(id, checked, type) => this.onFilterCheck(id, checked, type)}
            />

            <div className="results-list">
                <ol className="schedule">
                    {days}
                </ol>
            </div>

        </Comp.Page.AdminPage>;
    }

    private async filterData() {
        const filteredCalendar: M.Event.CalendarDay[] = [];
        const urlQuery = QueryString.parse(this.props.location.search) as UrlQuery;

        // setup filters from url query
        const dayIds = (urlQuery.day || ",").split(",").filter((x) => x !== "").map((x) => parseInt(x, 10));
        const q = (urlQuery.q || "").toLowerCase();
        let isSessionSearch = false;

        this.state.calendar.forEach((x: M.Event.CalendarDay) => {
            const day = { ...x };
            let isFiltered = !dayIds.length || dayIds.indexOf(day.id) > -1;

            // search filtering logic
            if (isFiltered && q) {
                if (x.events.size === 0)
                    isFiltered = false;
                else {
                    // reset day events
                    day.events = List();

                    // iterate through sub events
                    x.events.forEach((x_evt) => {
                        if (!x_evt)
                            return;

                        const evt = { ...x_evt };
                        let eventIsFiltered = evt.title.toLowerCase().indexOf(q) > -1 || (evt.type || "").toLowerCase() === q;

                        if (!eventIsFiltered) {
                            // create sessions array
                            const children = List(evt.children).toArray();

                            // iterate through sessions
                            evt.children = children.filter((session) => {
                                if (!session)
                                    return false;

                                let sessionIsFiltered = session.title.toLowerCase().indexOf(q) > -1 || (session.type || "").toLowerCase() === q;
                                if (sessionIsFiltered)
                                    return true;

                                if (session.scheduledAbstract) {
                                    // try filtering by abstract
                                    const a = session.scheduledAbstract;
                                    sessionIsFiltered = (Utility.stripHtml(a.title) || "").toLowerCase().indexOf(q) > -1
                                        || a.primaryAuthors.map((u) => Utility.joinSkipEmpty([u.firstName, u.lastName], " "))
                                            .join(", ")
                                            .toLowerCase()
                                            .indexOf(q) > -1
                                        || (a.type || "").toLowerCase() === q;

                                    if (sessionIsFiltered)
                                        return true;
                                }

                                return false;
                            });

                            if (evt.children.length) {
                                eventIsFiltered = true;
                                isSessionSearch = true;
                            }
                        }

                        if (eventIsFiltered) {
                            day.events = day.events.push(evt);
                            isFiltered = true;
                        }
                    });

                    if (!day.events.size)
                        isFiltered = false;
                }
            }

            if (isFiltered) {
                filteredCalendar.push(day);
            }
        });

        await this.setStateAsync({
            ...this.state,
            doFilter: false,
            filteredCalendar: List(filteredCalendar),
            isSessionSearch,
        });
    }

    private async generateCalendarExport() {
        if (this.state.generatingCalExport)
            return;

        await this.setStateAsync({ ...this.state, generatingCalExport: true });
        const eventId = this.getEventId();
        const result = await this.props.api.post(Api.endpoints.admin.event.exportCalendar.withUrlParams({ eventId }));

        if (!this._isMounted)
            return;

        await this.setStateAsync({ ...this.state, generatingCalExport: false });

        if (result.isSuccess && result.data) {
            const endpoint =  Api.endpoints.file.tempFile.withUrlParams({ token: result.data });
            const path = Settings.ApiUrl + ApiEndpoint.getFormattedUrl(endpoint) + "?access_token=Bearer%20" + encodeURIComponent(this.props.authToken);
            DocumentHelper.postForm(path, {});
        }
        else {
            this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
        }
    }

    private async generateRsvpExport(eventId: number) {
        if (this.state.generatingRsvpExport)
            return;

        await this.setStateAsync({ ...this.state, generatingRsvpExport: true });
        const result = await this.props.api.post(Api.endpoints.admin.event.exportRsvp.withUrlParams({ eventId }));

        if (!this._isMounted)
            return;

        await this.setStateAsync({ ...this.state, generatingRsvpExport: false });

        if (result.isSuccess && result.data) {
            const endpoint =  Api.endpoints.file.tempFile.withUrlParams({ token: result.data });
            const path = Settings.ApiUrl + ApiEndpoint.getFormattedUrl(endpoint) + "?access_token=Bearer%20" + encodeURIComponent(this.props.authToken);
            DocumentHelper.postForm(path, {});
        }
        else {
            this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
        }
    }

    private getEventId(props?: Readonly<CombinedProps>) {
        return parseInt(props
            ? props.match.params.id
            : this.props.match.params.id, 10);
    }

    private getFilterTypeName(type: FilterType) {
        switch (type) {
            case "day": return "Jump to";
            default: return "Unknown";
        }
    }

    private async handleDelete() {
        if (this._confirmDelete === null)
            return;

        const evt = this._confirmDelete.getRef() as M.Event.SubEvent;
        if (evt) {
            const evtLevel = EventLevels.isSession(evt) ? "Session" : "Event";
            const result = await this.props.api.delete(Api.endpoints.admin.event.event.withUrlParams({ eventId: evt.id }));

            if (result.isSuccess) {
                this.props.notifySuccess(`${evtLevel} <strong>${evt.title}</strong><br />has been successfully deleted`);
                this.props.removeSubEvent(evt);
            }
            else {
                this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
            }

            if (this._confirmDelete)
                this._confirmDelete.close();
        }
    }

    private handleSearch(search: string) {
        this.setState({ ...this.state, search });

        if (this._searchTimeout)
            clearTimeout(this._searchTimeout);

        this._searchTimeout = setTimeout(async () => {
            // actual search
            this.props.replaceUrlQuery({ ["q"]: search });
        }, 275);
    }

    private async initializeCalendar(event: M.Event.Event | undefined, subEvents: List<M.Event.SubEvent>) {
        if (!event)
            return;

        let calendar: List<M.Event.CalendarDay> = List();
        const ends = Utility.fromApiDateAsMoment(event.endDate).startOf("day");
        const starts = Utility.fromApiDateAsMoment(event.startDate).startOf("day");
        const dayCount = ends.diff(starts, "days");
        let currentDay = moment(starts);

        const days: M.Search.SearchFilter[] = [];

        for (let d = 0; d <= dayCount; d += 1) {
            const id = d + 1;
            const currentDayFormatted = currentDay.format("M-D");
            const day: M.Event.CalendarDay = {
                day: moment(currentDayFormatted, "M-D"),
                dayFormatted_MD: currentDayFormatted,
                dayFormatted_dddMMMD: currentDay.format("ddd, MMM D"),
                dayFormatted_ddddMMMMD: currentDay.format("dddd, MMMM D"),
                events: subEvents
                    .filter((x: M.Event.SubEvent) => (Utility.fromApiDateAsMoment(x.startDate).format("M-D") === currentDayFormatted))
                    .toList(),
                id,
            };

            days.push({
                id,
                name: day.dayFormatted_ddddMMMMD,
                checked: false,
                count: 0,
            });

            // increment the current day
            currentDay = currentDay.add(1, "day");
            calendar = calendar.push(day);
        }

        await this.setStateAsync({ ...this.state, calendar, days });
        await this.updateAllFilterChecks(this.props.location.search);
    }

    private async initializeComponent() {
        const eventId = this.getEventId();
        await this.loadEventFromState(true);
        await this.loadSubEventsFromState(true);
        await this.initializeCalendar(this.state.event, this.state.subEvents);

        this.props.loadEvent(eventId);
        this.props.loadSubEvents(eventId);
    }

    private async loadEventFromState(isInitialCheck?: boolean) {
        const eventId = this.getEventId();
        const event = this.props.events.find((x: M.Event.Event) => x.id === eventId);

        if (event) {
            await this.setStateAsync({ ...this.state, event });

            if (!isInitialCheck)
                await this.initializeCalendar(event, this.state.subEvents);
        }

        if (eventId > 0 && !event && isInitialCheck)
            this.props.toggleLoadingEvent(eventId, true);

        if (eventId > 0 && !event && !isInitialCheck) {
            this.props.goto(Paths.AdminEvent.List);
        }
    }

    private async loadSubEventsFromState(isInitialCheck?: boolean) {
        const eventId = this.getEventId();
        const subEvents = this.props.subEvents
            .filter((x: M.Event.SubEvent) => x.parentId === eventId)
            .toList();

        if (!subEvents.size && isInitialCheck)
            this.props.toggleLoadingSubEvents(true);

        await this.setStateAsync({ ...this.state, subEvents });

        if (!isInitialCheck)
            await this.initializeCalendar(this.state.event, subEvents);
    }

    private async onFilterCheck(id: string | number, checked: boolean, type: FilterType) {
        const urlQuery = QueryString.parse(this.props.location.search);
        const filterIds = Utility.commaStringToIntArray(urlQuery[type]);
        Utility.addToOrRemoveFromArray(filterIds, id, checked);
        this.props.replaceUrlQuery({ [type]: filterIds.join(",") });
    }

    private async onSessionsSorted(parentId: number, sortEnd: RS.SortEnd) {
        const subEvent = this.state.subEvents.find((x) => x !== undefined && x.id === parentId);
        if (subEvent && subEvent.children.length) {
            const children = [ ...subEvent.children ];
            const origIdArray = subEvent.children.map((c) => c.id).join(",");
            subEvent.children = RS.arrayMove(children, sortEnd.oldIndex, sortEnd.newIndex);
            const newIdArray = subEvent.children.map((c) => c.id).join(",");

            if (origIdArray === newIdArray)
                return;

            this.props.updateSubEvent(subEvent);
            this.toggleLoading(true, 500);

            const body = {
                parentId,
                sortedEvents: subEvent.children.map((x, i) => ({
                    eventId: x !== undefined ? x.id : 0,
                    sort: i + 1,
                })),
            };

            const result = await this.props.api.put(Api.endpoints.admin.event.childrenSequence.withUrlParams({ eventId: parentId }), {
                body: JSON.stringify(body),
            });

            if (result.isSuccess && result.data) {
                subEvent.children = result.data;
                this.props.updateSubEvent(subEvent);
                this.props.notifySuccess("Sequence of sessions has been successfully saved.");
            }
            else {
                this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
            }

            if (!this._isMounted)
                return;

            this.toggleLoading(false);
        }
    }

    private onSubEventUpdated(evt: M.Event.SubEvent) {
        const eventId = this.getEventId();
        if (evt.parentId === eventId) {
            this.props.updateSubEvent(evt);
            this.props.notifySuccess(`Event <strong>${evt.title}</strong><br />has been successfully saved.`);
        }
        else {
            const parentEvent = this.state.subEvents.find((x) => x !== undefined && x.id === evt.parentId);
            if (parentEvent) {
                // update parent's end date if session's end date is greater.
                const parentEndDate = Utility.fromApiDateToValue(parentEvent.endDate);
                const sessionEndDate = Utility.fromApiDateToValue(evt.endDate);
                if (sessionEndDate > parentEndDate)
                    parentEvent.endDate = evt.endDate;

                let children = List(parentEvent.children);
                const childIdx = children.findIndex((x) => x !== undefined && x.id === evt.id);
                children = childIdx === -1
                    ? children.push(evt)
                    : children.set(childIdx, evt);

                parentEvent.children = children
                    .sortBy((x) => x !== undefined && Utility.fromApiDateToValue(x.startDate))
                    .toArray();

                this.props.updateSubEvent(parentEvent);
                this.props.notifySuccess(`Session <strong>${evt.title}</strong><br />has been successfully saved.`);
            }
        }
    }

    private openAbstractDetails(guid: string) {
        if (this._abstractDetailsModal)
            this._abstractDetailsModal.open(guid);
    }

    private openEventEditModal(parent: M.Event.IEvent, evt?: M.Event.SubEvent) {
        if (!this._eventEditModal || !this.state.event)
            return;

        this._eventEditModal.show(parent, evt);
    }

    private parseUrlEditParam(editParam: string) {
        // edit param must be of format "x,y", where x is the event parent ID, y is the child ID.
        // y can be 0 for new events.
        if (!editParam || editParam.indexOf(",") === -1)
            // not avalid edit param
            return;

        const ids = editParam.split(",").filter((x) => x !== "").map((x) => parseInt(x, 10));
        const parentId = ids[0];
        const eventId = ids[1];

        let parent: M.Event.IEvent | undefined;
        let evt: M.Event.SubEvent | undefined;

        if (parentId === this.getEventId()) {
            parent = this.state.event;
            evt = this.state.subEvents.find((x) => x !== undefined && x.id === eventId);
        }
        else {
            parent = this.state.subEvents.find((x) => x !== undefined && x.id === parentId);
            if (parent)
                evt = List(parent.children).find((x) => x !== undefined && x.id === eventId);
        }

        if (parent)
            this.openEventEditModal(parent, evt);
    }

    private toggleDeleteConfirmation(evt: M.Event.SubEvent) {
        if (this._confirmDelete !== null)
            this._confirmDelete.show(evt);
    }

    private async updateAllFilterChecks(query: string) {
        const urlQuery = QueryString.parse(query) as UrlQuery;
        if (urlQuery.edit) {
            this.parseUrlEditParam(urlQuery.edit);

            // if this is an edit request only, no need to do any filtering
            if (_.keys(urlQuery).length === 1)
                return;
        }
        else if (urlQuery.abstract) {
            try {
                const abstractId = parseInt(urlQuery.abstract, 10);
                const subEvt = M.Abstract.findEventWithScheduledAbstract(abstractId, this.state.subEvents.toArray());
                if (subEvt && subEvt.scheduledAbstract)
                    this.openAbstractDetails(subEvt.scheduledAbstract.guid);
            }
            catch (err) {}
        }

        // get filtering maps from state
        const days = [ ...this.state.days ];
        const appliedFilters: M.Search.AppliedFilter[] = [];

        this.updateFilterChecks(days, "day", appliedFilters, query);

        await this.setStateAsync({
            ...this.state,
            appliedFilters,
            days,
        });

        await this.setStateAsync({
            ...this.state,
            doFilter: true,
        });
    }

    /**
     * Resets checked filters based on current url query
     */
    private updateFilterChecks(filters: M.Search.SearchFilter[], type: FilterType, appliedFilters: M.Search.AppliedFilter[], query?: string) {
        query = query === undefined ? this.props.location.search : query;
        const urlQuery = QueryString.parse(query) as UrlQuery;
        const itemIds = Utility.commaStringToIntArray(urlQuery[type]);

        for (const item of filters) {
            item.checked = itemIds.indexOf(item.id) > -1;
            if (item.checked) {
                appliedFilters.push({ id: item.id, name: item.name, type, typeName: this.getFilterTypeName(type) });
            }
        }
    }

    private updateUrlAbstractParam(abstractId?: number) {
        this.props.replaceUrlQuery({
            abstract: abstractId ? abstractId : undefined,
            edit: undefined,
        });
    }

    private updateUrlEditParam(parentId?: number, eventId?: number) {
        this.props.replaceUrlQuery({
            abstract: undefined,
            edit: parentId && eventId !== undefined
                ? `${parentId},${eventId}`
                : undefined,
        });
    }
}

export const EditCalendar: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(EditCalendarComponent) as any);



// WEBPACK FOOTER //
// ./src/containers/adminEvent/editCalendar/EditCalendar.tsx